import { RouteRecordRaw } from 'vue-router'

import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import PerformanceLayoutNew from '@/modules/performance/layouts/PerformanceLayoutNew.vue'
import PerformanceAddOnEdit from '@/modules/performance/pages/PerformanceAddOnEdit.vue'
import PerformanceAddOns from '@/modules/performance/pages/PerformanceAddOns.vue'
import PerformanceDashboard from '@/modules/performance/pages/PerformanceDashboard.vue'
import PerformancePricingTiers from '@/modules/performance/pages/PerformancePricingTiers.vue'
import PerformanceReservationDetail from '@/modules/performance/pages/PerformanceReservationDetail.vue'
import PerformanceReservationEdit from '@/modules/performance/pages/PerformanceReservationEdit.vue'
import PerformanceReservationList from '@/modules/performance/pages/PerformanceReservationList.vue'
import PerformanceWalkIn from '@/modules/performance/pages/PerformanceReservationWalkIn.vue'
import PerformanceSchedule from '@/modules/performance/pages/PerformanceSchedule.vue'
import PerformanceSeatingTimes from '@/modules/performance/pages/PerformanceSeatingTimes.vue'
import PerformanceTable from '@/modules/performance/pages/PerformanceTable.vue'
import PerformanceTableEdit from '@/modules/performance/pages/PerformanceTableEdit.vue'
import { PERFORMANCE_PARAMS } from '@/modules/performance/stores/PerformanceReservationStore'
import AccountingSummary from '@/pages/venue/AccountingSummary.vue'
import FloorPlans from '@/pages/venue/FloorPlans.vue'
import GuestDetail from '@/pages/venue/GuestDetail.vue'
import ReportsOverview from '@/pages/venue/ReportsOverview.vue'
import ReservationsDashboard from '@/pages/venue/ReservationsDashboard.vue'
import ShowTimes from '@/pages/venue/ShowTimes.vue'
import StyleGuide from '@/pages/venue/StyleGuide.vue'
import PerformanceAdd from '@/pages/venue/performances/PerformanceAdd.vue'
import ShowAddEdit from '@/pages/venue/shows/ShowAddEdit.vue'
import ShowDetail from '@/pages/venue/shows/ShowDetail.vue'
import ShowList from '@/pages/venue/shows/ShowList.vue'

import { UserGroup } from '@generated/types'

export const venueRoutes: RouteRecordRaw[] = [
  {
    path: '/venue/floorplans',
    name: 'floorPlans',
    component: FloorPlans,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Floor Plans',
    },
    children: [
      {
        path: ':floorPlanId',
        name: 'floorPlan',
        component: FloorPlans,
        meta: {
          pageTitle: 'Floor Plan',
        },
      },
    ],
  },
  {
    path: '/venue/showtimes',
    name: 'showtimes',
    component: ShowTimes,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Show Times',
    },
  },
  {
    path: '/venue/shows',
    name: 'showList',
    component: ShowList,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Show List',
    },
  },
  {
    path: '/venue/shows/add',
    name: 'showAdd',
    component: ShowAddEdit,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Add Show',
    },
  },
  {
    path: '/venue/shows/:id',
    name: 'showDetail',
    component: ShowDetail,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Show Detail',
    },
  },
  {
    path: '/venue/shows/:id/edit',
    name: 'showEdit',
    component: ShowAddEdit,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Edit Show',
    },
  },
  {
    path: '/venue/shows/:showId/performance/add',
    name: 'performanceAdd',
    component: PerformanceAdd,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Add Performance',
    },
  },
  {
    path: '/venue/performance/:performanceId',
    name: 'performanceDetail',
    redirect: { name: PERFORMANCE_ROUTE_NAMES.DASHBOARD },
  },
  {
    path: '/venue/performance-new/:performanceId',
    component: PerformanceLayoutNew,
    props: route => ({ performanceId: +route.params.performanceId }),
    children: [
      { path: '', redirect: { name: PERFORMANCE_ROUTE_NAMES.SCHEDULE } },
      {
        path: 'schedule/',
        name: PERFORMANCE_ROUTE_NAMES.SCHEDULE,
        component: PerformanceSchedule,
        meta: { pageTitle: 'Performance - Schedule' },
      },
      {
        path: 'tables/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.TABLES,
            component: PerformanceTable,
            meta: { pageTitle: 'Performance - Table List' },
          },
          {
            path: ':tableId',
            name: PERFORMANCE_ROUTE_NAMES.TABLE_EDIT,
            component: PerformanceTableEdit,
            props: route => ({
              tableId: isNaN(Number(route.params.tableId))
                ? route.params.tableId
                : Number(route.params.tableId),
            }),
            meta: { pageTitle: 'Performance - Table Detail' },
          },
        ],
      },
      {
        path: 'seating-times/',
        name: PERFORMANCE_ROUTE_NAMES.SEATING_TIMES,
        component: PerformanceSeatingTimes,
        meta: { pageTitle: 'Performance - Seating Times' },
      },
      {
        path: 'add-ons/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.ADD_ONS,
            component: PerformanceAddOns,
            meta: { pageTitle: 'Performance - Add-ons' },
          },
          {
            path: ':addOnId',
            name: PERFORMANCE_ROUTE_NAMES.ADD_ON_EDIT,
            component: PerformanceAddOnEdit,
            props: route => ({
              addOnId: Number(route.params.addOnId),
            }),
            meta: { pageTitle: 'Performance - Add-on Detail' },
          },
        ],
      },
      {
        path: 'pricing-tiers/',
        name: PERFORMANCE_ROUTE_NAMES.PRICING_TIERS,
        component: PerformancePricingTiers,
        meta: { pageTitle: 'Performance - Pricing Tiers' },
      },
      {
        path: 'dashboard/',
        name: PERFORMANCE_ROUTE_NAMES.DASHBOARD,
        component: PerformanceDashboard,
        meta: { pageTitle: 'Performance - Dashboard' },
      },
      {
        path: 'reservations/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
            component: PerformanceReservationList,
            meta: { pageTitle: 'Performance - Reservation List' },
          },
          {
            path: 'walk-in',
            name: PERFORMANCE_ROUTE_NAMES.RESERVATION_WALK_IN,
            component: PerformanceWalkIn,
            meta: { pageTitle: 'Performance - Reservation Walk-in' },
          },
          {
            path: ':reservationId',
            children: [
              {
                path: '',
                name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
                component: PerformanceReservationDetail,
                props: route => ({
                  reservationId: +route.params.reservationId,
                  performanceId: +route.params.performanceId,
                }),
                meta: { pageTitle: 'Performance - Reservation Detail' },
              },
              {
                path: 'edit',
                name: PERFORMANCE_ROUTE_NAMES.RESERVATION_EDIT,
                component: PerformanceReservationEdit,
                props: route => ({
                  reservationId: +route.params.reservationId,
                  performanceId: +route.params.performanceId,
                }),
                meta: { pageTitle: 'Performance - Reservation Edit' },
              },
            ],
          },
        ],
      },
    ],
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Performance',
    },
  },
  {
    path: '/venue/reservations/performance/:performanceId',
    name: 'reservationsPerformance',
    redirect: { name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST },
  },
  {
    path: '/venue/reservations',
    name: 'reservations',
    redirect: {
      name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
      params: { performanceId: PERFORMANCE_PARAMS.EMPTY },
    },
  },
  {
    path: '/venue/dashboard',
    name: 'reservationsDashboard',
    component: ReservationsDashboard,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Dashboard',
    },
  },
  {
    path: '/venue/reports/overview',
    name: 'reportsOverview',
    // @ts-ignore
    component: ReportsOverview,
    meta: {
      requiresAuth: UserGroup.OWNERS,
      pageTitle: 'Reports Overview',
    },
  },
  {
    path: '/venue/accounting-summary',
    name: 'accountingSummary',
    component: AccountingSummary,
    meta: {
      requiresAuth: UserGroup.OWNERS,
      pageTitle: 'Accounting Summary',
    },
  },
  {
    path: '/venue/guest/:id',
    name: 'guest',
    component: GuestDetail,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Guest Detail',
    },
  },
  {
    path: '/venue/style-guide',
    name: 'styleguide',
    component: StyleGuide,
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Style Guide',
    },
  },
]
